<script>
	import 'croppie/croppie.css';

	export default {
		name: 'ImageCropper',
		props: {
			inputSelector: {
				type: String,
				required: false
			},
			previewSelector: {
				type: String,
				default: null
			},
			croppieConfig: {
				type: Object,
				default: () => {}
			},
			droppedFile: {
				type: File,
				required: false
			},
			hideCancel: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				isCropperModalShowing: false,
				croppieInstance: null,
				targetInputElement: null,
				targetPreviewElement: null,
				currentFile: null,
				previousFile: null
			};
		},
		methods: {
			attachCropperToImageInput() {
				const input = document.querySelector(this.inputSelector);
				this.targetInputElement = input;

				if (input == null) return;

				if (this.previewSelector) this.targetPreviewElement = document.querySelector(this.previewSelector);

				input.addEventListener('change', async () => {
					const [file] = input.files;

					if (file.type.includes('svg')) return;

					// Very important we keep track, so when we cancel it will keep the old image
					this.previousFile = this.currentFile;
					this.currentFile = file;

					if (!file) {
						// need to clear out any previous preview if any
						if (!this.targetPreviewElement) return;
						this.targetPreviewElement.src = '#';
						this.targetPreviewElement.classList.add('hide');
						return;
					}

					await this.openImageCropper(URL.createObjectURL(file), this.croppieConfig);
				});
			},

			async openImageCropper(imageUrl, croppieConfig = {}) {
				const croppieContainer = this.$refs.cropperContainer;

				if (this.croppieInstance) this.croppieInstance.destroy();

				this.croppieInstance = new this.$croppie(croppieContainer, {
					url: imageUrl,
					viewport: {
						width: 480,
						height: 280,
						type: 'square'
					},
					boundary: {
						width: 600,
						height: 600
					},
					enableExif: true,
					enableOrientation: true,
					enforceBoundary: false,
					...croppieConfig
				});

				this.isCropperModalShowing = true;
			},
			setTargetImage(file) {
				const container = new DataTransfer();
				if (file) container.items.add(file);
				this.targetInputElement.files = container.files;
			},
			handleCancelBtn() {
				this.setTargetImage(this.previousFile);
				this.$emit('cancel', this.previousFile);
				this.isCropperModalShowing = false;
			},
			async handleCropBtn() {
				const blob = await this.croppieInstance.result('blob');

				// Add image to preview
				if (this.targetPreviewElement) {
					this.targetPreviewElement.src = URL.createObjectURL(blob);
				}

				// Update the input with the new image
				const newFile = new File([blob], this.currentFile.name, {
					type: this.currentFile.type,
					lastModified: new Date().getTime()
				});

				this.$emit('cropped', newFile);
				this.setTargetImage(newFile);
				this.isCropperModalShowing = false;
			}
		},
		watch: {
			isCropperModalShowing(newVal) {
				if (!this.croppieInstance || !this.currentFile || !newVal) return;

				// we need to wait for the animation to finish
				setTimeout(() => {
					// Croppie calculates based on position. We have to tell it to recalculate once modal is fully open!
					// Otherwise scrolling is cooked D:
					this.croppieInstance.bind({ url: URL.createObjectURL(this.currentFile) });
				}, 300);
			},
			droppedFile: {
				immediate: true,
				handler(newFile) {
					if (newFile) {
						this.openImageCropper(URL.createObjectURL(newFile), this.croppieConfig);
						this.currentFile = newFile;
					}
				}
			}
		},
		mounted() {
			this.attachCropperToImageInput();
		}
	};
</script>

<template>
	<v-dialog v-model="isCropperModalShowing" max-width="fit-content" persistent>
		<v-card>
			<v-card-title class="headline">Crop Image</v-card-title>
			<v-card-text>
				<div ref="cropperContainer"></div>
			</v-card-text>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn v-if="!hideCancel" flat @click="handleCancelBtn">Cancel</v-btn>
				<v-btn color="primary" flat @click="handleCropBtn">
					Crop
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<style scoped></style>
